import React, { useEffect, useRef, useState } from "react";
import {
    Avatar, Box, Chip, CircularProgress, Dialog, Divider,
    IconButton, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import CheckCircleRoundedIcon   from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon        from "@mui/icons-material/CancelRounded";
import PauseCircleRoundedIcon   from "@mui/icons-material/PauseCircleRounded";
import CheckRoundedIcon         from "@mui/icons-material/CheckRounded";
import PauseRoundedIcon         from "@mui/icons-material/PauseRounded";
import DownloadRoundedIcon      from "@mui/icons-material/DownloadRounded";
import CloseRoundedIcon         from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon    from "@mui/icons-material/AttachFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ZoomInRoundedIcon        from "@mui/icons-material/ZoomInRounded";
import ChevronRightRoundedIcon  from "@mui/icons-material/ChevronRightRounded";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { approvalApi } from "./api/approvalApi";
import AttachmentPreviewModal from "../mail/components/AttachmentPreviewModal";
import { isPreviewable } from "../../component/common/AttachmentPreviewList";
import {
    FORM_TYPES, LINE_STATUS_META, STATUS_META, fileIcon, formatSize,
} from "./approvalConstants";
import { runApiAction, serverMsgOr } from "../../util/apiFeedback";
import { confirm as confirmDialog } from "../../util/confirmDialog";
import ActionButton from "../../component/common/ActionButton";

dayjs.locale("ko");

/* ── StatusBadge ─────────────────────────────────────── */
function StatusBadge({ status }) {
    const m = STATUS_META[status] || STATUS_META.PENDING;
    return (
        <Box sx={{
            display: "inline-flex", alignItems: "center",
            px: 1, py: 0.2, borderRadius: 1,
            bgcolor: m.bg, color: m.color, fontSize: 12, fontWeight: 700,
        }}>
            {m.label}
        </Box>
    );
}

/* ── SectionCard — 헤더 밴드 + 본문 (레퍼런스 카드 톤) ── */
function SectionCard({ title, action, children, bodySx, sx }) {
    return (
        <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden", bgcolor: "#fff", ...sx }}>
            {title && (
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 2, py: 1.15, bgcolor: "#fafbfc", borderBottom: "1px solid #eef2f6",
                }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#475569", letterSpacing: "0.01em" }}>
                        {title}
                    </Typography>
                    {action}
                </Box>
            )}
            <Box sx={{ p: 2, ...bodySx }}>{children}</Box>
        </Box>
    );
}

/* ── SourceApprovalRef — 대체휴무의 근거가 된 대체근무(휴일근무) 결재 미리보기 + 이동 ── */
function SourceApprovalRef({ ids = [], text, onOpen }) {
    const [items, setItems]     = useState([]);
    const [loading, setLoading] = useState(false);

    const idList = Array.isArray(ids) ? ids.filter(Boolean) : [];

    useEffect(() => {
        if (!idList.length) { setItems([]); return; }
        let cancelled = false;
        setLoading(true);
        Promise.all(idList.map((id) =>
            approvalApi.detail(id)
                .then((r) => { const a = r?.data ?? r; return { id, title: a?.title, type: a?.type, status: a?.status, form: a?.form }; })
                .catch(() => ({ id, missing: true }))
        ))
            .then((res) => { if (!cancelled) setItems(res); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(idList)]);

    /* 근거 결재 부제: 유형 · 근무일 · 시간 */
    const subLine = (it) => {
        const d = it.form || {};
        const typeLabel = FORM_TYPES.find((f) => f.type === it.type)?.label;
        const when = d.date ? `${dayjs(d.date).format("YYYY.MM.DD")}${d.hours ? ` · ${d.hours}시간` : ""}` : "";
        return [typeLabel, when].filter(Boolean).join(" · ");
    };

    return (
        <Box sx={{ py: 1.1, borderBottom: "1px solid #f1f5f9", "&:last-child": { borderBottom: "none" } }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748b", mb: 0.6 }}>대체 근무 결재 근거</Typography>

            {loading && <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>불러오는 중…</Typography>}

            {!loading && !idList.length && (
                <Typography sx={{ fontSize: 14, color: "#334155" }}>{text || "—"}</Typography>
            )}

            {!loading && idList.length > 0 && (
                <Stack spacing={0.75}>
                    {items.map((it) => (
                        <Box key={it.id} sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            px: 1.25, py: 0.9, borderRadius: 1.5,
                            border: "1px solid #e2e8f0", bgcolor: "#f8fafc",
                        }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }} noWrap>
                                    {it.missing ? `결재 #${it.id} · 조회 불가` : (it.title || `결재 #${it.id}`)}
                                </Typography>
                                {!it.missing && subLine(it) && (
                                    <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.1 }} noWrap>{subLine(it)}</Typography>
                                )}
                            </Box>
                            {onOpen && !it.missing && (
                                <ActionButton variant="secondary"
                                    onClick={() => onOpen(it.id)}
                                    startIcon={<ChevronRightRoundedIcon sx={{ fontSize: 16 }} />}
                                    sx={{ flexShrink: 0, fontSize: 12, py: 0.4, px: 1 }}
                                    label="결재로 이동" />
                            )}
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

/* ── FormFields — 라벨 : 값 행 레이아웃 ───────────────── */
function FormFields({ type, form, onOpenApproval, applicant }) {
    const data = form || {};

    /* 라벨(위, 진하게) — 값(아래). value 는 문자열/숫자 또는 노드 모두 허용. */
    const Row = (label, value) => (
        <Box key={label} sx={{
            py: 1.1, borderBottom: "1px solid #f1f5f9",
            "&:last-child": { borderBottom: "none" },
        }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748b", mb: 0.6 }}>{label}</Typography>
            <Box sx={{ minWidth: 0 }}>
                {(typeof value === "string" || typeof value === "number")
                    ? <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1e293b", wordBreak: "break-word" }}>{value || "—"}</Typography>
                    : (value || <Typography sx={{ fontSize: 14, color: "#1e293b" }}>—</Typography>)}
            </Box>
        </Box>
    );

    /* 긴 본문(사유/내용): 라벨 위(진하게), 값 아래 */
    const MultiRow = (label, value) => (
        <Box key={label} sx={{
            py: 1.1, borderBottom: "1px solid #f1f5f9",
            "&:last-child": { borderBottom: "none" },
        }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748b", mb: 0.6 }}>{label}</Typography>
            <Typography sx={{
                fontSize: 14, color: "#334155", whiteSpace: "pre-wrap",
                wordBreak: "break-word", lineHeight: 1.65,
            }}>{value || "—"}
            </Typography>
        </Box>
    );

    const toComma = (v) => {
        const num = String(v || "").replace(/,/g, "").replace(/[^\d]/g, "");
        if (!num) return "";
        return Number(num).toLocaleString("ko-KR");
    };

    const fmtDatetime = (v) => v ? dayjs(v).format("YYYY.MM.DD HH:mm") : "—";
    const fmtDate     = (v) => v ? dayjs(v).format("YYYY.MM.DD") : "—";

    const durationStr = (start, end) => {
        if (!start || !end) return null;
        const mins = dayjs(end).diff(dayjs(start), "minute");
        if (mins <= 0) return null;
        const days  = Math.floor(mins / 1440);
        const hours = Math.floor((mins % 1440) / 60);
        const rem   = mins % 60;
        const parts = [];
        if (days  > 0) parts.push(`${days}일`);
        if (hours > 0) parts.push(`${hours}시간`);
        if (rem   > 0) parts.push(`${rem}분`);
        return parts.join(" ");
    };

    const summaryBox = (text, color = "#7e22ce", bg = "#fdf4ff", border = "#bfdbfe") => (
        <Box sx={{ mt: 1.25, px: 1.5, py: 1, bgcolor: bg, borderRadius: 1.5, border: `1px solid ${border}` }}>
            <Typography sx={{ fontSize: 12.5, color, fontWeight: 700 }}>{text}</Typography>
        </Box>
    );

    const applicantRow = Row("신청자", (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: "#2563eb" }}>
                {applicant?.[0] || "?"}
            </Avatar>
            <Typography sx={{ fontSize: 14, color: "#1e293b", fontWeight: 600 }}>
                {applicant || "—"}
            </Typography>
        </Box>
    ));

    switch (type) {
        case "VACATION": {
            const vt = data.vacationType;
            const isHalfDay  = vt === "반차";
            const isCompDay  = vt === "대체휴무";
            const isHourly   = vt === "시간연차";
            const isDatetime = vt && vt !== "연차" && !isHalfDay && !isCompDay && !isHourly;
            const dur = isDatetime ? durationStr(data.startDate, data.endDate) : null;
            return (
                <Box>
                    {applicantRow}
                    {Row("휴가 종류", vt)}
                    {isHalfDay ? (
                        <>
                            {Row("날짜", fmtDate(data.startDate))}
                            {Row("반차 구분", data.halfDayType)}
                        </>
                    ) : isCompDay ? (
                        <>
                            {Row("보상 방식", data.redeemType === "CASH" ? "현금 정산" : "휴가 사용")}
                            {Row("사용 단위", data.unit)}
                            {data.redeemType === "CASH"
                                ? Row("정산 금액", data.hours === 4 ? "50,000원" : "100,000원")
                                : Row("날짜", fmtDate(data.startDate))}
                            {(data.sourceApprovalIds?.length > 0 || data.sourceApprovalText) && (
                                <SourceApprovalRef key="src-ref" ids={data.sourceApprovalIds}
                                    text={data.sourceApprovalText} onOpen={onOpenApproval} />
                            )}
                        </>
                    ) : isHourly ? (
                        <>
                            {Row("날짜", fmtDate(data.startDate))}
                            {Row("시작 시간", data.startTime)}
                            {Row("종료 시간", data.endTime)}
                        </>
                    ) : isDatetime ? (
                        <>
                            {Row("시작 일시", fmtDatetime(data.startDate))}
                            {Row("종료 일시", fmtDatetime(data.endDate))}
                        </>
                    ) : (
                        <>
                            {Row("시작일", fmtDate(data.startDate))}
                            {Row("종료일", fmtDate(data.endDate))}
                        </>
                    )}
                    {isHourly && data.hours > 0 && summaryBox(
                        `${fmtDate(data.startDate)} ${data.startTime || ""}~${data.endTime || ""} · ${data.hours}시간 연차 사용`,
                        "#0369a1", "#f0f9ff", "#bae6fd"
                    )}
                    {dur && summaryBox(
                        `${fmtDatetime(data.startDate)} ~ ${fmtDatetime(data.endDate)}  ·  ${dur}`,
                        "#0369a1", "#f0f9ff", "#bae6fd"
                    )}
                    {MultiRow("사유", data.reason)}
                </Box>
            );
        }
        case "PAYMENT":
            return (
                <Box>
                    {applicantRow}
                    {Row("거래처명", data.vendor)}
                    {Row("금액 (원)", data.amount ? `${toComma(data.amount)} 원` : "")}
                    {Row("지급 예정일", data.paymentDate)}
                    {MultiRow("지급 내용", data.description)}
                </Box>
            );
        case "PURCHASE":
            return (
                <Box>
                    {applicantRow}
                    {Row("품목명", data.item)}
                    {Row("수량", data.quantity)}
                    {Row("금액 (원)", data.amount ? `${toComma(data.amount)} 원` : "")}
                    {MultiRow("구매 사유", data.reason)}
                    {(data.vendorUrl || data.vendorName) && (() => {
                        const v = data.vendorUrl || data.vendorName;            // 통합 필드(vendorUrl) 우선, 옛 데이터는 vendorName 폴백
                        const isUrl = /^https?:\/\//i.test(v) || /^[\w.-]+\.[a-z]{2,}([/?#].*)?$/i.test(String(v).trim());
                        return Row("구매처", isUrl ? (
                            <Box component="a"
                                href={/^https?:\/\//i.test(v) ? v : `https://${v}`}
                                target="_blank" rel="noreferrer"
                                sx={{ fontSize: 14, fontWeight: 600, color: "#2563eb", textDecoration: "none",
                                    wordBreak: "break-all", "&:hover": { textDecoration: "underline" } }}>
                                {v}
                            </Box>
                        ) : v);
                    })()}
                </Box>
            );
        case "BUSINESS_TRIP": {
            const btDur = durationStr(data.startDate, data.endDate);
            return (
                <Box>
                    {applicantRow}
                    {Row("출장지", data.destination)}
                    {Row("출발 일시", fmtDatetime(data.startDate))}
                    {Row("복귀 일시", fmtDatetime(data.endDate))}
                    {Row("예상 비용 (원)", data.cost ? `${toComma(data.cost)} 원` : "")}
                    {btDur && summaryBox(`${fmtDatetime(data.startDate)} ~ ${fmtDatetime(data.endDate)}  ·  ${btDur}`)}
                    {MultiRow("출장 목적", data.purpose)}
                </Box>
            );
        }
        case "OVERTIME": {
            const otDur = data.date && data.startTime && data.endTime
                ? durationStr(`${data.date}T${data.startTime}`, `${data.date}T${data.endTime}`)
                : null;
            return (
                <Box>
                    {applicantRow}
                    {Row("날짜", fmtDate(data.date))}
                    {Row("시작 시간", data.startTime)}
                    {Row("종료 시간", data.endTime)}
                    {otDur && summaryBox(
                        `${data.date}  ${data.startTime} – ${data.endTime}  ·  ${otDur}`,
                        "#be123c", "#fff1f2", "#fecdd3"
                    )}
                    {MultiRow("사유", data.reason)}
                </Box>
            );
        }
        case "HOLIDAY_WORK": {
            const hrs = Number(data.hours) || 0;
            return (
                <Box>
                    {applicantRow}
                    {Row("근무일", fmtDate(data.date))}
                    {Row("시작 시간", data.startTime)}
                    {Row("종료 시간", data.endTime)}
                    {hrs > 0 && Row("실 근무시간", `${hrs}시간`)}
                    {hrs > 0 && Row("적립 대체휴무", hrs >= 8 ? "1일 (8시간)" : "적립 없음 (8시간 미만)")}
                    {MultiRow("사유", data.reason)}
                </Box>
            );
        }
        default:
            return (
                <Box>
                    {applicantRow}
                    {Row("제목", data.title)}
                    {MultiRow("내용", data.content)}
                </Box>
            );
    }
}

/* ── ApprovalLineRail — 세로 진행선 (레퍼런스 스텝퍼) ── */
function ApprovalLineRail({ lines = [], approval }) {
    if (!lines?.length) {
        return <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>결재 라인이 없습니다.</Typography>;
    }

    const join = (...parts) => parts.filter(Boolean).join(" · ");

    const submitterNode = {
        label: approval?.submitterName || "-",
        sub: join(approval?.submitterPosition, approval?.submitterDept),
        subLabel: "신청",
        status: "SUBMIT",
        initial: approval?.submitterName?.[0] || "?",
        color: "#2563eb",
        bgColor: "#2563eb",
    };

    const nodes = [
        submitterNode,
        ...lines.map((line, idx) => {
            const meta = LINE_STATUS_META[line.status] || LINE_STATUS_META.WAITING;
            const bgColor =
                line.status === "APPROVED" ? "#16a34a" :
                line.status === "REJECTED" ? "#dc2626" :
                line.status === "HELD"     ? "#d97706" : "#2563eb";
            return {
                label: line.approverName || "-",
                sub: join(line.approverPosition, line.approverDept),
                subLabel: meta.label,
                status: line.status,
                initial: line.approverName?.[0] || "?",
                color: meta.color,
                bgColor,
                isFinal: idx === lines.length - 1,
                isActive: line.status === "PENDING",
                comment: line.comment,
                actionAt: line.actionAt,
            };
        }),
    ];

    const circleSx = (node) => ({
        width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, fontWeight: 700,
        bgcolor: node.status === "WAITING" ? "#f1f5f9" : node.bgColor,
        color:   node.status === "WAITING" ? "#94a3b8" : "#fff",
        border:  node.isActive ? "2px solid #bfdbfe" : "2px solid transparent",
        boxShadow: node.isActive ? "0 0 0 4px rgba(37,99,235,0.12)" : "none",
    });

    const segBorder = (status) =>
        status === "APPROVED" ? "2px solid #22c55e" :
        status === "SUBMIT"   ? "2px solid #93c5fd" :
        "2px dashed #cbd5e1";

    const circleIcon = (status) =>
        status === "APPROVED" ? <CheckRoundedIcon sx={{ fontSize: 20 }} /> :
        status === "REJECTED" ? <CloseRoundedIcon sx={{ fontSize: 20 }} /> :
        status === "HELD"     ? <PauseRoundedIcon sx={{ fontSize: 20 }} /> : null;

    return (
        <Box>
            {nodes.map((node, idx) => {
                const last = idx === nodes.length - 1;
                return (
                    <Box key={`${node.label}-${idx}`} sx={{ display: "flex", gap: 1.5 }}>
                        {/* 좌측 레일: 원 + 연결선 */}
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Box sx={circleSx(node)}>
                                {circleIcon(node.status) || node.initial}
                            </Box>
                            {!last && (
                                <Box sx={{ width: 0, flexGrow: 1, minHeight: 26, my: 0.5, borderLeft: segBorder(node.status) }} />
                            )}
                        </Box>

                        {/* 우측 내용 */}
                        <Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 2.25 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }} noWrap>
                                    {node.label}
                                </Typography>
                                {node.isFinal && (
                                    <Box sx={{
                                        fontSize: 10, fontWeight: 700, color: "#b45309",
                                        bgcolor: "#fffbeb", border: "1px solid #fde68a",
                                        px: 0.6, py: 0.1, borderRadius: 1,
                                    }}>
                                        최종
                                    </Box>
                                )}
                            </Stack>

                            {node.sub && (
                                <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.1 }} noWrap>{node.sub}</Typography>
                            )}

                            <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: node.color }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: node.color }}>{node.subLabel}</Typography>
                                {node.actionAt && (
                                    <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>
                                        · {dayjs(node.actionAt).format("MM.DD HH:mm")}
                                    </Typography>
                                )}
                            </Stack>

                            {node.comment && (
                                <Box sx={{ mt: 0.8, px: 1.25, py: 0.75, bgcolor: "#f8fafc", border: "1px solid #eef2f6", borderRadius: 1.5 }}>
                                    <Typography sx={{ fontSize: 12, color: "#475569", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                        {node.comment}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

/* ── ApprovalDetail (default export) ────────────────── */
export default function ApprovalDetail({ approval, myId, onRefresh, onDelete, adminMode = false, onOpenApproval }) {
    const [commentText, setCommentText] = useState("");
    const [actionDialog, setActionDialog] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [previewAtt, setPreviewAtt] = useState(null);
    const fileInputRef = useRef();

    const myLine         = approval.lines?.find((l) => l.approverId === myId && (l.status === "PENDING" || l.status === "HELD"));
    const isMyPending    = myLine?.status === "PENDING";
    const isMyHeld       = myLine?.status === "HELD";
    const canApproveOrReject = !adminMode && (isMyPending || isMyHeld);
    const canHold        = !adminMode && isMyPending;
    // 관리자 모드: 결재선 무관하게 강제 승인/거절 (미완료 상태일 때만)
    const canAdminAct    = adminMode && ["PENDING", "IN_PROGRESS", "HELD"].includes(approval.status);
    const isSubmitter    = approval.submitterId === myId;
    const anyApproved    = approval.lines?.some((l) => l.status === "APPROVED");
    const canDelete      = isSubmitter && !anyApproved;

    const isFinanceApproval  = approval.type === "PAYMENT" || approval.type === "PURCHASE";
    const isFinalFinanceTurn = Boolean(isFinanceApproval && myLine && myLine.stepOrder === approval.totalSteps);

    const typeInfo = FORM_TYPES.find((f) => f.type === approval.type);

    const handleDelete = async () => {
        if (!(await confirmDialog({ message: "결재를 삭제하시겠습니까?\n결재 요청이 취소되며 복구할 수 없습니다." }))) return;
        return runApiAction({
            request: () => approvalApi.deleteApproval(approval.id),
            successMsg: "결재가 삭제되었습니다.",
            errorMsg: (msg) => "삭제 실패: " + msg,
            onSuccess: () => onDelete?.(),
        });
    };

    const handleAction = () => runApiAction({
        request: () => {
            if (actionDialog === "approve")     return adminMode ? approvalApi.adminApprove(approval.id, commentText) : approvalApi.approve(approval.id, commentText);
            else if (actionDialog === "reject")  return adminMode ? approvalApi.adminReject(approval.id, commentText)  : approvalApi.reject(approval.id, commentText);
            else                                 return approvalApi.hold(approval.id, commentText);
        },
        successMsg: actionDialog === "approve" ? (adminMode ? "강제 승인했습니다." : "승인했습니다.")
            : actionDialog === "reject" ? (adminMode ? "강제 거절했습니다." : "반려했습니다.")
            : "보류했습니다.",
        errorMsg: (msg) => "처리 실패: " + msg,
        onSuccess: () => { setActionDialog(null); setCommentText(""); onRefresh(); },
    });

    const handleFileUpload = async (files) => {
        await runApiAction({
            request: async () => {
                let lastResponse = null;
                for (const f of files) lastResponse = await approvalApi.uploadAttachment(approval.id, f);
                return lastResponse;
            },
            errorMsg: serverMsgOr("파일 업로드 실패"),
            successMsg: "파일이 첨부되었습니다.",
            setLoading: setUploading,
            onSuccess: onRefresh,
        });
    };

    const handleDeleteAttachment = async (id) => {
        if (!(await confirmDialog({ message: "첨부파일을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => approvalApi.deleteAttachment(id),
            errorMsg: serverMsgOr("삭제 실패"),
            onSuccess: onRefresh,
        });
    };

    return (
        <Box>
            {/* 문서 헤더 (아이콘 없음) */}
            <Box sx={{ pb: 2, mb: 2.5, borderBottom: "1px solid #eef2f6" }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={0.8} flexWrap="wrap">
                            <StatusBadge status={approval.status} />
                            <Chip label={typeInfo?.label} size="small"
                                sx={{ fontSize: 11, height: 21, fontWeight: 700, bgcolor: `${typeInfo?.color}14`, color: typeInfo?.color }} />
                        </Stack>
                        <Typography sx={{ fontWeight: 800, fontSize: 22, color: "#0f172a", lineHeight: 1.3 }}>
                            {approval.title}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.5 }}>
                            {approval.submitterName} · {approval.submitterPosition}
                            {approval.submitterDept ? ` · ${approval.submitterDept}` : ""}
                            {" · "}{dayjs(approval.createdAt).format("YYYY-MM-DD HH:mm")}
                        </Typography>
                    </Box>
                    {canDelete && (
                        <ActionButton variant="delete"
                            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                            onClick={handleDelete}
                            sx={{ flexShrink: 0, fontSize: 11, py: 0.4 }}
                            label="결재 취소" />
                    )}


                </Stack>

            </Box>



            {/* 본문: 좌 결재 라인 / 우 내용+첨부(한 박스). 좌우 세로 길이 동일(stretch). */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "stretch", gap: 2.5 }}>


                {/* 우측: 내용 / 첨부 / 액션 */}
                <Box sx={{ flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* 재무 자동 반영 안내 */}


                    {/* 결재 내용 + 첨부파일 — 한 박스에서 구분선으로 분리 */}
                    <SectionCard title="결재 내용" bodySx={{ pt: 0.8, pb: 1.2 }} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <FormFields type={approval.type} form={approval.form}
                            onOpenApproval={onOpenApproval} applicant={approval.submitterName} />

                        <Divider sx={{ mt: 0, mb: 1.1, borderColor: "#f1f5f9" }} />

                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#475569", letterSpacing: "0.01em" }}>
                                첨부파일 ({approval.attachments?.length || 0})
                            </Typography>
                            {approval.status !== "APPROVED" && approval.status !== "REJECTED" && (
                                <Tooltip title="파일 추가">
                                    <IconButton size="small" disabled={uploading} onClick={() => fileInputRef.current?.click()} sx={{ p: 0.4 }}>
                                        {uploading
                                            ? <CircularProgress size={14} />
                                            : <AttachFileRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />}
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                        <input ref={fileInputRef} type="file" hidden multiple
                            onChange={(e) => { handleFileUpload(Array.from(e.target.files)); e.target.value = ""; }} />

                        {approval.attachments?.length ? (
                            <Stack spacing={0.75}>
                                {approval.attachments.map((att) => (
                                    <Box key={att.id} sx={{
                                        display: "flex", alignItems: "center", gap: 1,
                                        p: "8px 10px", borderRadius: 1.5,
                                        border: "1px solid #e2e8f0", bgcolor: "#f8fafc",
                                    }}>
                                        <Box sx={{ fontSize: 20, display: "flex" }}>{fileIcon(att.contentType)}</Box>
                                        <Box flex={1} minWidth={0}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600 }} noWrap>{att.originalFileName}</Typography>
                                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{formatSize(att.fileSize)}</Typography>
                                        </Box>
                                        {((att.contentType || "").startsWith("image/") || isPreviewable(att)) && (
                                            <Tooltip title="미리보기">
                                                <IconButton size="small" onClick={() => setPreviewAtt(att)} sx={{ p: 0.2 }}>
                                                    <ZoomInRoundedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Box component="a" href={att.fileUrl} target="_blank" rel="noreferrer" sx={{ display: "flex" }}>
                                            <DownloadRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                        </Box>
                                        {att.uploadedAt && (
                                            <IconButton size="small" onClick={() => handleDeleteAttachment(att.id)} sx={{ p: 0.2 }}>
                                                <CloseRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>첨부파일 없음</Typography>
                        )}
                    </SectionCard>
                </Box>

                {/* 좌측: 결재 라인 */}
                <Box sx={{ width: { xs: "100%", md: 300 }, flexShrink: 0, display: "flex" }}>
                    <SectionCard title="결재 라인" sx={{ flex: 1 }}>
                        <ApprovalLineRail lines={approval.lines} approval={approval} />
                    </SectionCard>
                </Box>

            </Box>

            {previewAtt && (
                <AttachmentPreviewModal open
                    attachment={(previewAtt.contentType || "").startsWith("image/")
                        ? { filename: previewAtt.originalFileName, mimeType: previewAtt.contentType, url: previewAtt.fileUrl }
                        : { id: previewAtt.id, filename: previewAtt.originalFileName, mimeType: previewAtt.contentType }}
                    loader={(previewAtt.contentType || "").startsWith("image/") ? undefined : () => approvalApi.previewAttachment(previewAtt.id)}
                    onClose={() => setPreviewAtt(null)} />
            )}

            {/* 승인/반려/보류 버튼 */}
            {(canApproveOrReject || canHold) && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                    {canApproveOrReject && <ActionButton variant="primary" startIcon={<CheckCircleRoundedIcon />}
                        onClick={() => setActionDialog("approve")}
                        sx={{ flex: 1, py: 0.9, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
                        label={isFinalFinanceTurn ? "결재 승인" : "승인"} />}
                    {canApproveOrReject && <ActionButton variant="primary" startIcon={<CancelRoundedIcon />}
                        onClick={() => setActionDialog("reject")}
                        sx={{ flex: 1, py: 0.9, bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }}
                        label="반려" />}
                    {canHold && <ActionButton variant="secondary" startIcon={<PauseCircleRoundedIcon />}
                        onClick={() => setActionDialog("hold")}
                        sx={{ flex: 1, py: 0.9, color: "#d97706", borderColor: "#fcd34d",
                            "&:hover": { bgcolor: "#fffbeb", borderColor: "#d97706" } }}
                        label="보류" />}
                </Stack>
            )}

            {/* 관리자 강제 승인/거절 — 결재선 무시 */}
            {canAdminAct && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                    <ActionButton variant="primary" startIcon={<CheckCircleRoundedIcon />}
                        onClick={() => setActionDialog("approve")}
                        sx={{ flex: 1, py: 0.9, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
                        label="강제 승인" />
                    <ActionButton variant="primary" startIcon={<CancelRoundedIcon />}
                        onClick={() => setActionDialog("reject")}
                        sx={{ flex: 1, py: 0.9, bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }}
                        label="강제 거절" />
                </Stack>
            )}

            {/* 액션 확인 다이얼로그 */}
            <Dialog open={Boolean(actionDialog)} onClose={() => setActionDialog(null)} maxWidth="xs" fullWidth>
                <Box sx={{ p: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>
                        {actionDialog === "approve"
                            ? isFinalFinanceTurn ? "결재 승인 및 재무 반영" : "승인"
                            : actionDialog === "reject" ? "반려"
                            : "⏸ 보류"}{" "}하시겠습니까?
                    </Typography>

                    <TextField fullWidth multiline minRows={2} size="small" placeholder="의견 (선택)"
                        value={commentText} onChange={(e) => setCommentText(e.target.value)}/>

                    <Stack direction="row" spacing={1} mt={2} justifyContent="flex-end">
                        <ActionButton variant="cancel" onClick={() => setActionDialog(null)} />
                        {/* 확인 버튼은 actionDialog 종류별로 다른 색을 가져야 해서 sx override (승인=초록, 반려=빨강, 보류=주황). */}
                        <ActionButton variant="confirm" onClick={handleAction} startIcon={false}/>
                    </Stack>
                </Box>
            </Dialog>
        </Box>
    );
}
